import {
  User,
  Calendar,
  Clock,
  Loader2,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Quote,
} from "lucide-react";
import { useOverview } from "@/hooks/overview";
import { CircularProgress } from "../CircuralProgres";
import { formatDate } from "@/data/overview";

const Overview: React.FC = () => {
  const {
    navigate,
    userName,
    greeting,
    courses,
    upcomingClass,
    assessments,
    loading,
    feedbacks,
    currentFeedbackIndex,
    currentFeedback,
    displayedAssessments,
    displayedCourses,
    handleNextFeedback,
    handlePrevFeedback,
  } = useOverview();

  return (
    <main className="w-full px-8 py-10 font-poppins bg-white min-h-screen text-[#0F172A]">
      {/* 1. HEADER */}
      <div className="mb-8">
        <p className="text-gray-500 text-lg font-medium">Hello, {userName}</p>
        <h1 className="text-4xl font-bold text-black mt-1 tracking-tight">
          {greeting}
        </h1>
      </div>

      {/* 2. BANNER NOTIFIKASI */}
      <div className="w-full bg-[#ECFDF5] border border-[#D1FAE5] rounded-2xl p-6 mb-10 shadow-sm">
        <p className="text-[#065F46] text-sm text-center font-medium">
          If there are any technical or system issues, please contact the
          assigned class trainer or the Fluenesia admin
        </p>
      </div>

      {/* 3. GRID LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* === KOLOM KIRI === */}
        <div className="lg:col-span-2 space-y-12">
          {/* COURSE LIST */}
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#1E293B]">
                Course You’re Taking
              </h2>
              {/* Button View All Courses */}
              {courses.length > 0 && (
                <button
                  onClick={() =>
                    navigate("/user/dashboard", { state: { tab: "My Class" } })
                  }
                  className="text-[#E32626] font-bold text-sm hover:underline transition-colors"
                >
                  View All
                </button>
              )}
            </div>

            <div className="space-y-6">
              {loading ? (
                <div className="h-40 bg-gray-50 rounded-3xl flex items-center justify-center text-gray-400">
                  <Loader2 className="animate-spin mr-2" /> Loading Courses...
                </div>
              ) : courses.length > 0 ? (
                displayedCourses.map((course) => {
                  const displayTitle = course.title
                    ? course.title
                    : course.level || "General";

                  // --- [LOGIKA BARU SESUAI MYCLASS.TSX] ---
                  // Kita cek 'certificate_file' karena itulah nama field dari API/Database
                  const hasCertificate =
                    course.certificate_file && course.certificate_file !== "";

                  // Jika sertifikat ada, progress otomatis 100
                  const finalProgress = hasCertificate
                    ? 100
                    : course.progress || 0;

                  // Jika sertifikat ada, teks berwarna HIJAU
                  const progressColor = hasCertificate
                    ? "text-[#10B981]"
                    : "text-[#0F172A]";

                  return (
                    <div
                      key={course.id}
                      className="bg-white border border-gray-200 rounded-[24px] p-6 flex flex-col md:flex-row gap-8 shadow-sm hover:shadow-md transition-shadow"
                    >
                      {/* Gambar Kursus */}
                      <div className="w-full md:w-[150px] h-[110px] bg-[#E32626] rounded-2xl relative overflow-hidden flex-shrink-0">
                        <div className="absolute inset-0 bg-[url('/buku2.png')] bg-cover bg-center opacity-90 mix-blend-overlay"></div>
                        <img
                          src="/buku2.png"
                          alt="course"
                          className="absolute right-0 bottom-0 z-10 w-[85%] h-auto object-contain drop-shadow-lg"
                        />
                      </div>

                      {/* === INFO KURSUS === */}
                      <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-[1.5fr_1.2fr_0.8fr] gap-y-6 md:gap-x-6 items-start">
                        {/* KOLOM 1: Course & Completion */}
                        <div className="flex flex-col justify-between h-full gap-5">
                          <div>
                            <p className="text-[#94A3B8] text-[13px] font-medium mb-1.5">
                              Course
                            </p>
                            <p
                              className="text-[#0F172A] text-[16px] font-bold leading-tight line-clamp-2"
                              title={displayTitle}
                            >
                              {displayTitle}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#94A3B8] text-[13px] font-medium mb-1.5">
                              Completion
                            </p>
                            <div className="flex items-center gap-2">
                              <CircularProgress percentage={finalProgress} />

                              <span
                                className={`${progressColor} text-[16px] font-bold`}
                              >
                                {finalProgress}%
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* KOLOM 2: Content & Tutor */}
                        <div className="flex flex-col justify-between h-full gap-5">
                          <div>
                            <p className="text-[#94A3B8] text-[13px] font-medium mb-1.5">
                              Content
                            </p>
                            <p className="text-[#0F172A] text-[16px] font-bold">
                              {course.content_material || "0 Material"}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#94A3B8] text-[13px] font-medium mb-1.5">
                              Tutor
                            </p>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                <img
                                  src={`https://ui-avatars.com/api/?name=${course.tutor_name}&background=random`}
                                  alt="tutor"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <p
                                className="text-[#0F172A] text-[14px] font-bold truncate max-w-[120px]"
                                title={course.tutor_name}
                              >
                                {course.tutor_name || "Tutor"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* KOLOM 3: Status */}
                        <div>
                          <p className="text-[#94A3B8] text-[13px] font-medium mb-1.5">
                            Status
                          </p>
                          <p className="text-[#0F172A] text-[16px] font-bold capitalize">
                            {course.status || "Active"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-10 border-2 border-dashed border-gray-200 rounded-[32px] text-center text-gray-400 font-medium">
                  No active courses found.
                </div>
              )}
            </div>
          </section>

          {/* ASSESSMENT HISTORY */}
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#1E293B]">
                Recent Assessment
              </h2>
              {courses.length > 0 && (
                <button
                  onClick={() => navigate(`/user/class/${courses[0].id}`)}
                  className="text-[#E32626] font-bold text-sm hover:underline transition-colors"
                >
                  View All
                </button>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-[24px] shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-8 flex justify-center text-gray-400">
                  <Loader2 className="animate-spin mr-2" /> Loading
                  Assessments...
                </div>
              ) : assessments.length > 0 ? (
                displayedAssessments.map((item, i) => (
                  <div
                    key={i}
                    className="p-6 flex flex-col md:flex-row md:items-center justify-between border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors gap-6 md:gap-0"
                  >
                    <div className="flex items-center gap-5 md:w-1/3">
                      <div className="w-[52px] h-[52px] bg-red-50 rounded-2xl flex items-center justify-center text-[#E32626] shrink-0">
                        <ClipboardList size={26} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-400 font-medium mb-1">
                          Assessment
                        </p>
                        <p className="font-bold text-[#1E293B] text-[15px]">
                          {item.quizTitle || "Assessment Test"}
                        </p>
                      </div>
                    </div>
                    <div className="md:w-1/4">
                      <p className="text-[11px] text-gray-400 font-medium mb-1">
                        Date Taken
                      </p>
                      <p className="font-bold text-[14px] text-[#1E293B]">
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                    <div className="md:w-1/5">
                      <p className="text-[11px] text-gray-400 font-medium mb-1">
                        Status
                      </p>
                      <span className="inline-block px-3 py-1 rounded-md text-[11px] font-bold bg-green-100 text-green-600">
                        Done
                      </span>
                    </div>
                    <div>
                      <button
                        onClick={() =>
                          navigate("/user/dashboard", {
                            state: { tab: "Assessment" },
                          })
                        }
                        className="bg-[#FF0000] text-white px-8 py-2.5 rounded-full text-sm font-bold hover:bg-[#D90000] transition shadow-md shadow-red-100 w-full md:w-auto"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-400">
                  <p className="mb-4">You haven't taken any assessments yet.</p>
                  <button
                    onClick={() => navigate("/assessment/free-test")}
                    className="text-[#E32626] font-bold text-sm underline"
                  >
                    Take Placement Test
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* === KOLOM KANAN === */}
        <div className="space-y-12">
          {/* UPCOMING CLASS */}
          <section>
            <h2 className="text-2xl font-bold mb-6">Upcoming Class</h2>
            {loading ? (
              <div className="h-40 bg-gray-50 rounded-[24px] flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin mr-2" /> Check Schedule...
              </div>
            ) : upcomingClass ? (
              <div className="relative bg-white border border-gray-100 rounded-[24px] p-6 shadow-sm overflow-hidden hover:shadow-md transition-shadow group">
                <div className="absolute top-0 left-0 w-3 h-full bg-[#E32626]"></div>
                <div className="pl-5 flex flex-col h-full">
                  <h3 className="text-xl font-bold text-[#1E293B] mb-3 leading-tight">
                    {upcomingClass.title}
                  </h3>
                  <div className="flex items-center flex-wrap gap-3 text-gray-500 text-sm font-medium mb-6">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{upcomingClass.date}</span>
                    </div>
                    <span className="text-gray-300">|</span>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      <span>{upcomingClass.time}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 overflow-hidden border border-gray-200">
                        {upcomingClass.tutor_photo ? (
                          <img
                            src={upcomingClass.tutor_photo}
                            alt="Tutor"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User size={16} />
                        )}
                      </div>
                      <p className="text-sm font-bold text-[#475569]">
                        {upcomingClass.tutor_name || "Tutor"}
                      </p>
                    </div>
                    <button className="bg-[#FF0000] text-white px-8 py-2 rounded-full font-bold text-sm hover:bg-[#D90000] transition shadow-md shadow-red-100">
                      Join
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-gray-200 rounded-[24px] p-6 text-center text-gray-400">
                <p className="font-medium">No upcoming classes.</p>
              </div>
            )}
          </section>

          {/* FEEDBACK */}
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Feedback</h2>
              {feedbacks.length > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevFeedback}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-gray-600 transition"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={handleNextFeedback}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-gray-600 transition"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-100 rounded-[32px] p-8 shadow-sm relative min-h-[280px] flex flex-col justify-between">
              {currentFeedback ? (
                <div className="animate-in fade-in duration-300 w-full">
                  {/* Header: Avatar & Tutor Name */}
                  <div className="flex items-center gap-4 mb-5">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-[#1E293B] font-bold text-lg border border-transparent"
                      style={{ backgroundColor: "#EEF4FF" }}
                    >
                      {currentFeedback.tutor_name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .substring(0, 2)
                        .toUpperCase()}
                    </div>
                    <div>
                      <p className="font-extrabold text-[#1E293B] uppercase text-[15px] tracking-wide">
                        {currentFeedback.tutor_name || "TUTOR"}
                      </p>
                    </div>
                  </div>

                  {/* Message: break-words & w-full */}
                  <div className="relative w-full">
                    <Quote className="absolute -top-2 -left-2 text-gray-200 w-8 h-8 -z-10 opacity-50" />
                    <p className="text-[15px] text-[#475569] leading-relaxed mb-6 font-medium italic z-10 relative break-words w-full">
                      "{currentFeedback.feedback}"
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="pt-4 border-t border-gray-50 mt-auto flex justify-between items-center">
                    <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">
                      {currentFeedback.category}
                    </p>

                    {feedbacks.length > 1 && (
                      <div className="flex gap-1.5">
                        {feedbacks.map((_, idx) => (
                          <div
                            key={idx}
                            className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentFeedbackIndex ? "bg-[#E32626] w-3" : "bg-gray-200"}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 py-10">
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <User className="opacity-30" />
                  </div>
                  <p className="text-sm">No feedback available yet.</p>
                  <p className="text-xs mt-1">
                    Complete a course to get tutor feedback.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default Overview;
